import { SalesTargetFilters } from '../../../BusinessTargets/types'
import SalesPerformance from './SalesPerformance'
import EmployeeSalesContributions from './EmployeeSalesContributions'
import ServicesSalesContributions from './ServicesSalesContributions'
import EmployeeTable from './EmployeeTable'
import SalePerformanceBarChart from './SalePerformanceBarChart'
import StatCards from './StatCards'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { useAnalyticsTour } from './use-analytics-tour'
import { useTourHandler } from '@/contexts/tour-handler'
import { useEffect } from 'react'

interface AnalyticsProps {
    filters: SalesTargetFilters
}

export default function Analytics({ filters }: AnalyticsProps) {
    const { isAdmin } = usePermissionsStore()
    const hasEmployeeFilter =
        !!filters.employee_id && filters.employee_id.length > 0

    // Integrate Tour
    const { startTour } = useAnalyticsTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    if (isAdmin) {
        return (
            <div className="space-y-2">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
                    <div
                        className="lg:col-span-4 h-full"
                        data-tour="target-analytics-employee-table"
                    >
                        <EmployeeTable filters={filters} />
                    </div>
                    <div className="flex flex-col space-y-2 lg:col-span-8">
                        {/* Stat Cards Row */}
                        <div
                            className="shrink-0"
                            data-tour="target-analytics-stat-cards"
                        >
                            <StatCards filters={filters} />
                        </div>
                        <div
                            className="flex-1"
                            data-tour="target-analytics-performance-chart"
                        >
                            <SalePerformanceBarChart filters={filters} />
                        </div>
                    </div>
                </div>
                <div
                    className={`grid grid-cols-1 ${hasEmployeeFilter ? 'lg:grid-cols-12' : ''} gap-2`}
                >
                    <div
                        className={`flex flex-col ${hasEmployeeFilter ? 'lg:col-span-8' : ''}`}
                        data-tour="target-analytics-sales-performance"
                    >
                        <SalesPerformance filters={filters} />
                    </div>
                    {hasEmployeeFilter && (
                        <div className="lg:col-span-4">
                            <div className="grid grid-cols-1 gap-2 h-full">
                                <div data-tour="target-analytics-services-pie">
                                    <ServicesSalesContributions
                                        filters={filters}
                                    />
                                </div>
                                <div data-tour="target-analytics-employee-pie">
                                    <EmployeeSalesContributions
                                        filters={filters}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <div className="flex flex-col gap-2">
                    <div data-tour="target-analytics-sales-performance">
                        <SalesPerformance filters={filters} />
                    </div>
                    {hasEmployeeFilter && (
                        <div data-tour="target-analytics-services-pie">
                            <ServicesSalesContributions filters={filters} />
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    <div data-tour="target-analytics-stat-cards">
                        <StatCards filters={filters} />
                    </div>
                    <div data-tour="target-analytics-performance-chart">
                        <SalePerformanceBarChart filters={filters} />
                    </div>
                </div>
            </div>
        </div>
    )
}
